import React from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, Loader2, Save, FolderOpen, Trash2, X } from 'lucide-react';

import {
  DEFAULT_SPACER_STOCK,
  AVAILABLE_SPACERS,
  STATION_COUNTS,
  BOTTOM_TUBE_REFERENCE,
  allocateSpacersGlobally,
} from './spacer/stock';
import {
  TubeRow,
  GapElement,
  FillElement,
  StaticFill,
  PlateElement,
  PlatedSpacerBox,
  SpacerBox,
  SpacerRingCombo,
} from './spacer/primitives';
import { SideRollingStation } from './spacer/SideRollingStation';
import { LipRollingStation } from './spacer/LipRollingStation';
import { FeedingStation } from './spacer/FeedingStation';

// Re-export sub-components for backward-compatible imports
export { SideRollingStation, LipRollingStation, FeedingStation, DEFAULT_SPACER_STOCK };

// Main container for all spacer visualizations
const SpacerVisualization = ({ spacers, result, spacerStock, onStockChange }) => {
  const [showStockEditor, setShowStockEditor] = React.useState(false);
  const [allocation, setAllocation] = React.useState(null);
  const debounceTimer = React.useRef(null);
  
  // Stock config save/load state
  const [stockConfigs, setStockConfigs] = React.useState([]);
  const [showSaveDialog, setShowSaveDialog] = React.useState(false);
  const [showLoadDialog, setShowLoadDialog] = React.useState(false);
  const [newConfigName, setNewConfigName] = React.useState('');
  const [savingConfig, setSavingConfig] = React.useState(false);
  const [loadingConfigs, setLoadingConfigs] = React.useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Use provided stock or defaults
  const currentStock = spacerStock || DEFAULT_SPACER_STOCK;
  
  // Fetch stock configs
  const fetchStockConfigs = React.useCallback(async () => {
    setLoadingConfigs(true);
    try {
      const response = await fetch(`${API_URL}/api/stock-configs`);
      if (response.ok) {
        const data = await response.json();
        setStockConfigs(data);
      }
    } catch (error) {
      console.error('Error fetching stock configs:', error);
    } finally {
      setLoadingConfigs(false);
    }
  }, [API_URL]);
  
  // Save current stock as new config
  const saveStockConfig = async () => {
    if (!newConfigName.trim()) return;
    
    setSavingConfig(true);
    try {
      const response = await fetch(`${API_URL}/api/stock-configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newConfigName.trim(),
          stock: currentStock
        })
      });
      
      if (response.ok) {
        setNewConfigName('');
        setShowSaveDialog(false);
        await fetchStockConfigs();
      }
    } catch (error) {
      console.error('Error saving stock config:', error);
    } finally {
      setSavingConfig(false);
    }
  };
  
  // Load a stock config
  const loadStockConfig = (config) => {
    if (onStockChange && config.stock) {
      onStockChange(config.stock);
    }
    setShowLoadDialog(false);
  };
  
  // Delete a stock config
  const deleteStockConfig = async (configId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this stock configuration?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/stock-configs/${configId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchStockConfigs();
      }
    } catch (error) {
      console.error('Error deleting stock config:', error);
    }
  };
  
  // Load configs when load dialog opens
  React.useEffect(() => {
    if (showLoadDialog) {
      fetchStockConfigs();
    }
  }, [showLoadDialog, fetchStockConfigs]);
  
  // Debounced spacer allocation calculation
  const hasSpacers = !!spacers;
  const sBottom510 = spacers?.bottom_5_10;
  const sBottom24 = spacers?.bottom_2_4;
  const sFeedingBottom = spacers?.feedingBottom;
  const sLipGapLeft = spacers?.lipGapLeft;
  const sLipGapRight = spacers?.lipGapRight;
  React.useEffect(() => {
    if (!hasSpacers) return;

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      try {
        const result = allocateSpacersGlobally({
          bottom_5_10: sBottom510,
          bottom_2_4: (sLipGapLeft > 0 || sLipGapRight > 0) ? sBottom24 : 0,
          feedingBottom: sFeedingBottom
        }, currentStock);
        setAllocation(result);
      } catch (e) {
        console.error('Spacer allocation error:', e);
        setAllocation(null);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [hasSpacers, sBottom510, sBottom24, sFeedingBottom, sLipGapLeft, sLipGapRight, currentStock]);
  
  if (!spacers) return null;

  // Scale only depends on screen size, not print (CSS handles print scaling)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const scale = isMobile ? 1.8 : 2.88; // pixels per mm
  
  const hasLeftLip = spacers.lipGapLeft > 0;
  const hasRightLip = spacers.lipGapRight > 0;
  const hasLips = hasLeftLip || hasRightLip;
  const feedingExtra = 0.5;
  
  // Side Rolling display gaps (with feeding adjustment when no lips)
  const displayLeftGap = !hasLips ? spacers.setupDistanceLeft + feedingExtra : spacers.setupDistanceLeft;
  const displayRightGap = !hasLips ? spacers.setupDistanceRight + feedingExtra : spacers.setupDistanceRight;
  
  // Calculate tube extension widths for Side Rolling and lip gap widths for Lip Rolling
  // When a lip exists: show (lipGap + 0.5mm) for feeding alignment
  // When no lip but other side has lip: show just 0.5mm for feeding alignment
  // Widths are proportional to actual mm values - no minimum applied
  const leftExtWidth = hasLeftLip 
    ? (spacers.lipGapLeft + feedingExtra) * scale
    : (hasLips ? feedingExtra * scale : 0);
  const rightExtWidth = hasRightLip 
    ? (spacers.lipGapRight + feedingExtra) * scale
    : (hasLips ? feedingExtra * scale : 0);
  
  // Lip gap values for feeding alignment
  const leftLipGapValue = hasLeftLip ? spacers.lipGapLeft + feedingExtra : (hasLips ? feedingExtra : 0);
  const rightLipGapValue = hasRightLip ? spacers.lipGapRight + feedingExtra : (hasLips ? feedingExtra : 0);
  
  // Side Rolling totals (for feeding alignment when no lips)
  const sideRollingTopTotal = displayLeftGap + spacers.top_5_10 + displayRightGap;
  const sideRollingBottomTotal = displayLeftGap + spacers.bottom_5_10 + displayRightGap;
  
  // Lip Rolling totals (for feeding alignment when lips exist)
  const lipRollingTopTotal = leftLipGapValue + spacers.top_2_4 + rightLipGapValue;
  const lipRollingBottomTotal = leftLipGapValue + spacers.bottom_2_4 + rightLipGapValue;
  
  // Container widths for Lip Rolling blue box (natural proportional width)
  const topContainerWidth = (spacers.setupDistanceLeft + spacers.top_5_10 + spacers.setupDistanceRight) * scale;
  const bottomContainerWidth = (spacers.setupDistanceLeft + spacers.bottom_5_10 + spacers.setupDistanceRight) * scale;
  
  // Side Rolling element widths
  const topSpacerWidth = spacers.top_5_10 * scale;
  const bottomSpacerWidth = spacers.bottom_5_10 * scale;
  
  // Feeding alignment - not used for alignment anymore, just for spacer sizing
  const feedingAlignmentWidth = {
    top: spacers.feedingTop * scale,
    bottom: spacers.feedingBottom * scale
  };
  
  // === FILL DISTANCE CALCULATIONS (right side) ===
  // Fill = how many mm of spacer rings to stack from the right starting point
  const topTubeRef = BOTTOM_TUBE_REFERENCE + spacers.roundedThickness;
  
  // Station 5-10 fill - FIXED, never changes
  const sideRolling_topFillRight = topTubeRef;
  const sideRolling_bottomFillRight = BOTTOM_TUBE_REFERENCE; // always 110
  
  // Station 2-4 fill - 110 minus the setup distance from 5-10
  const lipRolling_topFillRight = topTubeRef - spacers.setupDistanceRight;
  const lipRolling_bottomFillRight = BOTTOM_TUBE_REFERENCE - spacers.setupDistanceRight;
  
  // Feeding fill - 110 minus setup distance minus lip gap
  const feeding_topFillRight = hasLips
    ? topTubeRef - spacers.setupDistanceRight - spacers.lipGapRight
    : topTubeRef - spacers.setupDistanceRight;
  const feeding_bottomFillRight = hasLips
    ? BOTTOM_TUBE_REFERENCE - spacers.setupDistanceRight - spacers.lipGapRight
    : BOTTOM_TUBE_REFERENCE - spacers.setupDistanceRight;
  
  // Fill visual widths - proportional to fill value, separate for top/bottom
  // Top fill is wider (by roundedThickness), centering the top spacer within the bottom spacer
  const sideRollingTopFillWidth = sideRolling_topFillRight * scale;
  const sideRollingBottomFillWidth = sideRolling_bottomFillRight * scale;
  const lipRollingTopFillWidth = lipRolling_topFillRight * scale;
  const lipRollingBottomFillWidth = lipRolling_bottomFillRight * scale;
  const feedingTopFillWidth = feeding_topFillRight * scale;
  const feedingBottomFillWidth = feeding_bottomFillRight * scale;
  
  // Left padding - separate for top and bottom to center top spacer within bottom
  // Bottom pad = bottomFill - leftContent (centers bottom spacer)
  // Top pad = topFill - leftContent (centers top spacer, extra roundedT*scale shifts it right)
  const sideRollingBottomLeftPad = hasLips
    ? Math.max(0, sideRollingBottomFillWidth - spacers.setupDistanceLeft * scale)
    : Math.max(0, sideRollingBottomFillWidth - displayLeftGap * scale);
  const sideRollingTopLeftPad = hasLips
    ? Math.max(0, sideRollingTopFillWidth - spacers.setupDistanceLeft * scale)
    : Math.max(0, sideRollingTopFillWidth - displayLeftGap * scale);
  
  const lipRollingBottomLeftPad = Math.max(0, lipRollingBottomFillWidth - leftExtWidth);
  const lipRollingTopLeftPad = Math.max(0, lipRollingTopFillWidth - leftExtWidth);
  
  const feedingBottomLeftPad = feedingBottomFillWidth;
  const feedingTopLeftPad = feedingTopFillWidth;

  // Handle stock quantity change
  const handleStockChange = (size, value) => {
    const numValue = parseInt(value) || 0;
    if (onStockChange) {
      onStockChange({
        ...currentStock,
        [size]: Math.max(0, numValue)
      });
    }
  };

  // Spacer color mapping for the editor
  const getSpacerColor = (size) => {
    const colors = {
      20: 'bg-rose-500',
      19: 'bg-amber-500',
      11: 'bg-emerald-500',
      9: 'bg-cyan-500',
      5: 'bg-violet-500',
    };
    return colors[size] || 'bg-gray-500';
  };

  // Common props for each station component — used to render multiple labeled
  // copies (one per physical roll-forming station on the line).
  const sideRollingProps = {
    spacers, scale, topSpacerWidth, bottomSpacerWidth, leftExtWidth,
    spacerAllocation: allocation?.sideRolling,
    topFillRight: sideRolling_topFillRight,
    bottomFillRight: sideRolling_bottomFillRight,
    topFillRightWidth: sideRollingTopFillWidth,
    bottomFillRightWidth: sideRollingBottomFillWidth,
    topLeftPad: sideRollingTopLeftPad,
    bottomLeftPad: sideRollingBottomLeftPad,
  };
  const lipRollingProps = {
    spacers, topContainerWidth, bottomContainerWidth, scale, leftExtWidth,
    spacerAllocation: allocation?.lipRolling,
    topFillRight: lipRolling_topFillRight,
    bottomFillRight: lipRolling_bottomFillRight,
    topFillRightWidth: lipRollingTopFillWidth,
    bottomFillRightWidth: lipRollingBottomFillWidth,
    topLeftPad: lipRollingTopLeftPad,
    bottomLeftPad: lipRollingBottomLeftPad,
  };
  const feedingProps = {
    spacers, scale,
    alignmentWidth: feedingAlignmentWidth,
    spacerAllocation: allocation?.feeding,
    topFillRight: feeding_topFillRight,
    bottomFillRight: feeding_bottomFillRight,
    topFillRightWidth: feedingTopFillWidth,
    bottomFillRightWidth: feedingBottomFillWidth,
    topLeftPad: feedingTopLeftPad,
    bottomLeftPad: feedingBottomLeftPad,
  };

  // Station-number lists driven by lip presence.
  //   Lips present (10 stations active):
  //     Feeding = Station 1, Lip Rolling = Stations 2-4, Side Rolling = 5-10.
  //   No lips (only 8 stations active):
  //     Stations 1 & 2 IDLE (skipped — no rings, not rendered).
  //     Feeding = Stations 3 & 4, Side Rolling = 5-10.
  //
  // Render order is REVERSED: Station 10 first, Station 1 last. Operators read
  // the setup sheet from the side-rolling end (where the profile exits) back
  // toward the feed end, so the natural reading order matches material flow
  // direction when the sheet is pinned next to the machine.
  const feedingStations = hasLips ? [1] : [3, 4];
  const lipStations = hasLips ? [2, 3, 4] : [];

  // Per-station allocations — `perStation` is keyed by station-type and is an
  // array in physical station order. We wrap the single station's entry in an
  // array so SpacerRingCombo (which expects an array of groups) renders just
  // ITS allocation, not the "×N stations" multiplier.
  const perStation = allocation?.perStation || {};
  const sideAlloc = perStation.sideRolling || [];
  const lipAlloc = perStation.lipRolling || [];
  const feedAlloc = perStation.feeding || [];
  // Map a station number to its specific feeding allocation entry.
  //   With lips:    Station 1   → feedAlloc[0]
  //   Without lips: Station 3   → feedAlloc[0]
  //                 Station 4   → feedAlloc[1]
  const feedForStation = (n) => {
    if (feedAlloc.length === 0) return [];
    const idx = hasLips ? 0 : (n === 3 ? 0 : 1);
    const entry = feedAlloc[idx];
    return entry ? [{ ...entry, count: 1 }] : [];
  };

  return (
    <div className="space-y-2 sm:space-y-3 print-stations">
      {/* Side Rolling — Stations 10-8 individually, then 5-7 grouped
          (identical config, ×3 badge = three ring sets, one per station) */}
      {[10, 9, 8].map((n) => (
        <SideRollingStation
          key={`side-${n}`}
          {...sideRollingProps}
          spacerAllocation={sideAlloc[n - 5] ? [{ ...sideAlloc[n - 5], count: 1 }] : []}
          title={`Station ${n} (Side Rolling)`}
        />
      ))}
      <SideRollingStation
        key="side-5-6-7"
        {...sideRollingProps}
        spacerAllocation={sideAlloc[0] ? [{ ...sideAlloc[0], count: 3 }] : []}
        title="Stations 5 · 6 · 7 (Side Rolling)"
      />

      {/* Lip Rolling — 3 labeled copies, 4 → 2 (only when lips exist) */}
      {[...lipStations].reverse().map((n) => (
        <LipRollingStation
          key={`lip-${n}`}
          {...lipRollingProps}
          spacerAllocation={lipAlloc[n - 2] ? [{ ...lipAlloc[n - 2], count: 1 }] : []}
          title={`Station ${n} (Lip Rolling)`}
        />
      ))}

      {/* Feeding — N labeled copies, ending at Station 1 */}
      {[...feedingStations].reverse().map((n) => (
        <FeedingStation
          key={`feed-${n}`}
          {...feedingProps}
          spacerAllocation={feedForStation(n)}
          title={`Station ${n} (Feeding)`}
        />
      ))}
      {/* Spacer Stock Editor - at bottom, hidden in print */}
      <div className="bg-zinc-800 rounded-lg border border-zinc-700 no-print">
        <button
          onClick={() => setShowStockEditor(!showStockEditor)}
          className="w-full flex items-center justify-between p-3 sm:p-4 text-left hover:bg-zinc-700 transition-colors rounded-lg"
          data-testid="toggle-stock-editor"
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className="font-semibold text-zinc-300 text-sm sm:text-base">Spacer Ring Stock</span>
          </div>
          {showStockEditor ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </button>
        
        {showStockEditor && (
          <div className="px-3 sm:px-4 pb-3 sm:pb-4">
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
              {AVAILABLE_SPACERS.map((size) => (
                <div key={size} className="flex flex-col items-center gap-1">
                  <div 
                    className={`${getSpacerColor(size)} text-white text-xs sm:text-sm font-bold w-8 h-6 sm:w-10 sm:h-7 flex items-center justify-center rounded`}
                  >
                    {size}
                  </div>
                  <Input
                    type="number"
                    value={currentStock[size] || 0}
                    onChange={(e) => handleStockChange(size, e.target.value)}
                    className="w-12 sm:w-14 h-7 sm:h-8 text-center text-xs sm:text-sm p-1 bg-zinc-700 border-zinc-600 text-zinc-100"
                    min={0}
                    data-testid={`stock-input-${size}`}
                  />
                </div>
              ))}
            </div>
            
            {/* Save/Load Buttons */}
            <div className="flex items-center justify-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSaveDialog(true)}
                className="text-xs gap-1 border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                data-testid="save-stock-btn"
              >
                <Save className="w-3 h-3" />
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLoadDialog(true)}
                className="text-xs gap-1 border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                data-testid="load-stock-btn"
              >
                <FolderOpen className="w-3 h-3" />
                Load
              </Button>
            </div>
            
            {/* Save Dialog */}
            {showSaveDialog && (
              <div className="mt-3 p-3 bg-zinc-900 rounded-lg border border-zinc-600 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-zinc-200">Save Configuration</span>
                  <button onClick={() => setShowSaveDialog(false)} className="text-zinc-400 hover:text-zinc-200">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newConfigName}
                    onChange={(e) => setNewConfigName(e.target.value)}
                    placeholder="Configuration name..."
                    className="flex-1 h-8 text-sm bg-zinc-800 border-zinc-600 text-zinc-100"
                    data-testid="stock-config-name-input"
                  />
                  <Button
                    size="sm"
                    onClick={saveStockConfig}
                    disabled={!newConfigName.trim() || savingConfig}
                    className="h-8 bg-white text-black hover:bg-zinc-200"
                    data-testid="confirm-save-stock-btn"
                  >
                    {savingConfig ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Load Dialog */}
            {showLoadDialog && (
              <div className="mt-3 p-3 bg-zinc-900 rounded-lg border border-zinc-600 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-zinc-200">Load Configuration</span>
                  <button onClick={() => setShowLoadDialog(false)} className="text-zinc-400 hover:text-zinc-200">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {loadingConfigs ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                  </div>
                ) : stockConfigs.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-3">No saved configurations</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {stockConfigs.map((config) => (
                      <div
                        key={config.id}
                        onClick={() => loadStockConfig(config)}
                        className="flex items-center justify-between p-2 rounded hover:bg-zinc-800 cursor-pointer group"
                        data-testid={`stock-config-${config.id}`}
                      >
                        <span className="text-sm text-zinc-300">{config.name}</span>
                        <button
                          onClick={(e) => deleteStockConfig(config.id, e)}
                          className="text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-stock-config-${config.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <p className="text-[10px] sm:text-xs text-zinc-500 mt-2 text-center">
              Edit available quantities for each spacer ring size (mm)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpacerVisualization;
